import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { api } from '@/lib/apiClient';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { customerFormSchema, type CustomerFormData } from '@/schemas/customerSchemas';
import type { Customer } from '@/types/legacy';
import { Loader2, AlertTriangle, Hash } from 'lucide-react';

interface CustomerFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customer?: Customer | null;
}

export function CustomerFormDialog({ open, onOpenChange, customer }: CustomerFormDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [nextNumber, setNextNumber] = useState<number | null>(null);
  const [codeDuplicate, setCodeDuplicate] = useState<string | null>(null);

  const form = useForm<CustomerFormData>({
    resolver: zodResolver(customerFormSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      company_name: '',
      codificacion: '',
      cif: '',
      email: '',
      phone: '',
      address: '',
      is_active: true,
      notes: '',
    },
  });

  // Reset form when customer changes or dialog opens
  useEffect(() => {
    if (open) {
      setCodeDuplicate(null);
      if (customer) {
        form.reset({
          first_name: customer.first_name || '',
          last_name: customer.last_name || '',
          company_name: customer.company_name || '',
          codificacion: (customer as any).codificacion || '',
          cif: (customer as any).cif || '',
          email: customer.email || '',
          phone: customer.phone || '',
          address: customer.address || '',
          is_active: customer.is_active ?? true,
          notes: customer.notes || '',
        });
      } else {
        form.reset({
          first_name: '',
          last_name: '',
          company_name: '',
          codificacion: '',
          cif: '',
          email: '',
          phone: '',
          address: '',
          is_active: true,
          notes: '',
        });
        // Fetch next customer number
        api.from('customers')
          .select('customer_number')
          .order('customer_number', { ascending: false })
          .limit(1)
          .then(({ data }) => {
            const maxNum = data?.[0]?.customer_number || 0;
            setNextNumber(maxNum + 1);
          });
      }
    }
  }, [customer, open, form]);

  // Validate code duplication on blur
  const checkCodeDuplicate = async (code: string) => {
    if (!code || code.trim() === '') {
      setCodeDuplicate(null);
      return;
    }
    const { data } = await api
      .from('customers')
      .select('id, company_name, codificacion')
      .ilike('codificacion', code.trim());

    const existing = data?.find(c =>
      c.id !== customer?.id &&
      c.codificacion?.toLowerCase() === code.toLowerCase()
    );

    if (existing) {
      setCodeDuplicate(existing.company_name || existing.codificacion);
    } else {
      setCodeDuplicate(null);
    }
  };

  const onSubmit = async (data: CustomerFormData) => {
    // Check for code duplicate before saving
    if (codeDuplicate) {
      toast({
        title: 'Codificación duplicada',
        description: `La codificación "${data.codificacion}" ya existe para "${codeDuplicate}"`,
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...data,
        first_name: data.first_name || null,
        last_name: data.last_name || null,
        company_name: data.company_name || null,
        codificacion: data.codificacion || null,
        cif: data.cif || null,
        email: data.email || null,
        phone: data.phone || null,
        address: data.address || null,
        notes: data.notes || null,
      };

      if (customer) {
        const { error } = await api
          .from('customers')
          .update(payload)
          .eq('id', customer.id);

        if (error) throw error;

        toast({
          title: 'Cliente actualizado',
          description: 'El cliente se actualizó correctamente',
        });
      } else {
        const { error } = await api
          .from('customers')
          .insert([payload]);

        if (error) throw error;

        toast({
          title: 'Cliente creado',
          description: 'El cliente se creó correctamente',
        });
      }

      queryClient.invalidateQueries({ queryKey: ['customers'] });
      window.dispatchEvent(new CustomEvent('customer-updated'));
      onOpenChange(false);
      form.reset();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el cliente',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle>{customer ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle>
            <Badge variant="outline" className="gap-1 font-mono bg-primary/10">
              C-{customer ? String((customer as any).customer_number).padStart(3, '0') : nextNumber ? String(nextNumber).padStart(3, '0') : '...'}
            </Badge>
          </div>
          {!customer && nextNumber && (
            <DialogDescription>
              Se asignará el código <strong>C-{String(nextNumber).padStart(3, '0')}</strong>
            </DialogDescription>
          )}
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="first_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Juan" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="last_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Apellido</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: Pérez" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="company_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Empresa</FormLabel>
                  <FormControl>
                    <Input {...field} placeholder="Ej: Empresa ABC" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="codificacion"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Codificación (Siglas)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Ej: CLI-001"
                        onBlur={(e) => {
                          field.onBlur();
                          checkCodeDuplicate(e.target.value);
                        }}
                        className={codeDuplicate ? 'border-destructive' : ''}
                      />
                    </FormControl>
                    {codeDuplicate && (
                      <p className="text-sm text-destructive flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Ya existe en "{codeDuplicate}"
                      </p>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="cif"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CIF / NIF</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: B12345678" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Correo Electrónico</FormLabel>
                    <FormControl>
                      <Input {...field} type="email" placeholder="correo@ejemplo.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Teléfono</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Ej: +52 1234567890" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="address"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Dirección</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Dirección completa" rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notas</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Notas adicionales" rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Activo</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      El cliente está activo en el sistema
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {customer ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
